import { useMemo, useState } from "react";
import {
    Alert, Button, Card, Empty, Modal, Select, Space, Switch, Table, Tag, Tooltip, Typography, message
} from "antd";
import {
    DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined
} from "@ant-design/icons";
import API from "../../API/API";
import { useDomainGroups } from "../../Context/DomainGroupsContext";
import DomainGroupModal from "./DomainGroupModal";

const { Text, Title } = Typography;

const TIER_COLORS = {
    "TIER-1": "blue",
    "TIER-2": "gold",
    "TIER-3": "purple"
};

const DomainMapping = () => {
    const { groups, loading, error, refresh } = useDomainGroups();

    const [tierFilter, setTierFilter] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [togglingId, setTogglingId] = useState(null);

    const rows = useMemo(() => (
        tierFilter ? groups.filter((group) => group.tier === tierFilter) : groups
    ), [groups, tierFilter]);

    const openCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const openEdit = (group) => {
        setEditing(group);
        setModalOpen(true);
    };

    const toggleVisible = async (group, visible) => {
        setTogglingId(group._id);

        try {
            const response = await API.updateDomainGroup(group._id, { visible });

            if (response?.success) {
                message.success(visible ? `"${group.name}" column shown` : `"${group.name}" column hidden`);
                await refresh();
            } else {
                message.error(response?.message || "Could not update visibility");
            }
        } catch (err) {
            message.error(err?.message || "Could not update visibility");
        } finally {
            setTogglingId(null);
        }
    };

    const confirmDelete = (group) => {
        Modal.confirm({
            title: `Delete "${group.name}"?`,
            icon: <ExclamationCircleOutlined />,
            okText: "Delete",
            okButtonProps: { danger: true },
            content: (
                <div>
                    <p>
                        <strong>{group.domains?.length || 0}</strong> domain
                        {group.domains?.length === 1 ? "" : "s"} will stop routing to this group, and{" "}
                        <strong>{group.agentCount || 0}</strong> agent
                        {group.agentCount === 1 ? "" : "s"} will be removed from it.
                    </p>
                    <p style={{ marginBottom: 0 }}>
                        Leads already assigned from this group keep their history. Domains no group
                        claims fall through to the Tier-1 catch-all group.
                    </p>
                </div>
            ),
            onOk: async () => {
                try {
                    const response = await API.deleteDomainGroup(group._id);

                    if (response?.success) {
                        message.success(response.message || `"${group.name}" deleted`);
                        await refresh();
                    } else {
                        message.error(response?.message || "Could not delete the group");
                    }
                } catch (err) {
                    message.error(err?.message || "Could not delete the group");
                }
            }
        });
    };

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            width: 180,
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.agentCount || 0} agent{record.agentCount === 1 ? "" : "s"}
                    </Text>
                </Space>
            )
        },
        {
            title: "Key",
            dataIndex: "key",
            key: "key",
            width: 140,
            render: (key, record) => (
                <Tooltip title={`Round robin cursor: ${record.rrKey}`}>
                    <Text code>{key}</Text>
                </Tooltip>
            )
        },
        {
            title: "Tier",
            dataIndex: "tier",
            key: "tier",
            width: 110,
            render: (tier) => <Tag color={TIER_COLORS[tier]}>{tier}</Tag>
        },
        {
            title: "Domains",
            dataIndex: "domains",
            key: "domains",
            render: (domains = []) => (
                <Space size={[4, 4]} wrap>
                    {domains.map((domain) => <Tag key={domain}>{domain}</Tag>)}
                </Space>
            )
        },
        {
            title: "Visible",
            dataIndex: "visible",
            key: "visible",
            width: 100,
            align: "center",
            render: (visible, record) => (
                <Tooltip title="Controls the Team page column only. A hidden group still routes leads.">
                    <Switch
                        size="small"
                        checked={visible !== false}
                        loading={togglingId === record._id}
                        onChange={(checked) => toggleVisible(record, checked)}
                    />
                </Tooltip>
            )
        },
        {
            title: "Order",
            dataIndex: "order",
            key: "order",
            width: 80,
            align: "center"
        },
        {
            title: "Actions",
            key: "actions",
            width: 110,
            align: "center",
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => confirmDelete(record)} />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Space style={{ width: "100%", justifyContent: "space-between" }} align="start">
                    <div>
                        <Title level={4} style={{ marginBottom: 4 }}>Domain Mapping</Title>
                        <Text type="secondary">
                            Which domains route to which group of agents. Changes take effect within a minute.
                        </Text>
                    </div>

                    <Space>
                        <Select
                            allowClear
                            placeholder="All tiers"
                            style={{ width: 150 }}
                            value={tierFilter}
                            onChange={setTierFilter}
                            options={["TIER-1", "TIER-2", "TIER-3"].map((tier) => ({ value: tier, label: tier }))}
                        />
                        <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>Refresh</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add New Group</Button>
                    </Space>
                </Space>

                <Alert
                    type="warning"
                    showIcon
                    message="New domains must also be added to the socket.io allowlist in Server.js (getCorsOrigins), or real-time updates will fail."
                />

                {error && <Alert type="error" showIcon message={error} />}

                <Card bodyStyle={{ padding: 0 }}>
                    <Table
                        rowKey="_id"
                        columns={columns}
                        dataSource={rows}
                        loading={loading}
                        pagination={false}
                        size="middle"
                        locale={{
                            emptyText: (
                                <Empty
                                    description={
                                        tierFilter
                                            ? `No groups in ${tierFilter}`
                                            : "No domain groups yet. Run Scripts/seedDomainGroups.js, or add one here."
                                    }
                                />
                            )
                        }}
                    />
                </Card>
            </Space>

            <DomainGroupModal
                open={modalOpen}
                group={editing}
                onClose={() => setModalOpen(false)}
                onSaved={refresh}
            />
        </div>
    );
};

export default DomainMapping;
